// WHAT: Dashboard Hero Component (Organism)
// WHY:  Main hero section displaying key metrics and progress visualization
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: StreakDisplay, ProgressVisualization, QuickStatsGrid molecules, UI components
// RISKS:
//   - NOTE: Layout assumes specific grid spans (2 cols for streak, 1 for progress)
//   - NOTE: Milestone props are optional (may show 0 if not provided)

import React from "react";

import { Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { ProgressVisualization } from "../molecules/ProgressVisualization";
import { QuickStatsGrid } from "../molecules/QuickStatsGrid";
import { StreakDisplay } from "../molecules/StreakDisplay";

interface DashboardHeroProperties {
  className?: string;
  completionRate: number;
  nextMilestone?: number;
  sessionsUntilMilestone?: number;
  streak: number;
  thisWeekSessions: number;
  totalSessions: number;
}

/**
 * WHAT:
 *   Hero section organism aggregating key dashboard metrics and visualizations.
 *
 * WHY:
 *   Provides prominent display of user progress and motivation at top of dashboard.
 *
 * PROPS:
 *   - streak: number — Current streak count
 *   - completionRate: number — Completion rate percentage
 *   - totalSessions: number — Total completed sessions
 *   - thisWeekSessions: number — Sessions completed this week
 *   - nextMilestone?: number — Next milestone target (optional)
 *   - sessionsUntilMilestone?: number — Sessions remaining (optional)
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays hero header with title and description
 *   - Shows StreakDisplay in 2-column span (large screens)
 *   - Shows ProgressVisualization in 1-column span (large screens)
 *   - Displays QuickStatsGrid below hero metrics
 *   - Responsive layout: 1 col mobile, 3 col desktop
 *
 * RISKS:
 *   - NOTE: Grid layout assumes specific component sizes (may break on very small screens)
 *   - NOTE: Milestone props are optional - may show 0 in QuickStatsGrid if not provided
 *   - NOTE: Completion rate should be 0-100 but not validated
 *
 * @param root0
 *
 * @param root0.streak
 *
 * @param root0.completionRate
 *
 * @param root0.totalSessions
 *
 * @param root0.thisWeekSessions
 *
 * @param root0.nextMilestone
 *
 * @param root0.sessionsUntilMilestone
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const DashboardHero: React.FC<DashboardHeroProperties> = ({
  className,
  completionRate,
  nextMilestone,
  sessionsUntilMilestone,
  streak,
  thisWeekSessions,
  totalSessions,
}) => {
  return (
    <div className={cn("space-y-6", className)}>
      {/* Hero Header */}
      <Stack direction="col" gap={2}>
        <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
          Your Miracle Flow Journey
        </Heading>
        <p className="text-[#94A3B8] text-sm">
          Track your progress and stay motivated on your path to personal growth
        </p>
      </Stack>

      {/* Main Hero Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Streak Display - Takes 2 columns on large screens */}
        <div className="lg:col-span-2">
          <StreakDisplay streak={streak} />
        </div>

        {/* Progress Visualization */}
        <div className="lg:col-span-1">
          <ProgressVisualization completionRate={completionRate} />
        </div>
      </div>

      {/* Quick Stats Grid */}
      <QuickStatsGrid
        nextMilestone={nextMilestone}
        sessionsUntilMilestone={sessionsUntilMilestone}
        thisWeekSessions={thisWeekSessions}
        totalSessions={totalSessions}
      />
    </div>
  );
};
